import type { ChartUnit } from '../../../core/types/chart-unit.js';
import type { SeriesCurve } from '../../../core/types/timeseries.js';
import { getUnitAsString } from '../../../core/utils/get-unit-as-string.js';
import { XYChart } from '../../../xy-chart/XYChartRoot.js';
import type {
  XYAreaSeriesActions,
  XYBarSeriesActions,
  XYLineSeriesActions,
} from '../map-series-actions.js';
import type { SeriesProps } from '../transformer-data-to-xy.js';

export const xAxisId = 'x-axis';

export const defineAxisId = (unit: ChartUnit) =>
  `y-axis-${getUnitAsString(unit)}`;

const baseSeriesProps = ({ data, unit, seriesId }: SeriesProps) => ({
  data,
  xAxisId,
  yAxisId: defineAxisId(unit),
  seriesIdAccessor: 'seriesId',
  nameAccessor: 'seriesName',
  key: seriesId,
});

type LineSeriesOptions = { actions?: XYLineSeriesActions; curve?: SeriesCurve };
type AreaSeriesOptions = {
  actions?: XYAreaSeriesActions;
  curve?: SeriesCurve;
};
type BarSeriesOptions = { actions?: XYBarSeriesActions };
type BandSeriesOptions = { actions?: XYAreaSeriesActions; curve?: SeriesCurve };

export const renderLineSeries = (
  series: SeriesProps,
  options: LineSeriesOptions = {},
) => (
  <XYChart.LineSeries
    {...baseSeriesProps(series)}
    x0Accessor="resolvedCenter"
    x1Accessor="resolvedEnd"
    y0Accessor="value"
    color={series.color}
    pointsDisplay={series.pointsDisplay}
    curve={options.curve}
    actions={options.actions}
  />
);

export const renderAreaSeries = (
  series: SeriesProps,
  options: AreaSeriesOptions = {},
) => (
  <XYChart.AreaSeries
    {...baseSeriesProps(series)}
    x0Accessor="resolvedCenter"
    x1Accessor="resolvedEnd"
    y1Accessor="value"
    color={series.color}
    pointsDisplay={series.pointsDisplay}
    curve={options.curve}
    actions={options.actions}
  />
);

export const renderBarSeries = (
  series: SeriesProps,
  options: BarSeriesOptions = {},
) => (
  <XYChart.BarSeries
    {...baseSeriesProps(series)}
    x0Accessor="start"
    x1Accessor="end"
    variant="stack"
    y1Accessor="value"
    color={series.color}
    actions={options.actions}
  />
);

export const renderBandSeries = (
  series: SeriesProps,
  options: BandSeriesOptions = {},
) => (
  <XYChart.AreaSeries
    {...baseSeriesProps(series)}
    x0Accessor="start"
    x1Accessor="end"
    y0Accessor="y0"
    y1Accessor="y1"
    color={series.color}
    curve={options.curve}
    actions={options.actions}
  />
);
